export const STATUS_BADGES = {
  initiated: { label: 'Initiated', className: 'bg-gray-100 text-gray-700' },
  submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-700' },
  pending_review: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700' },
  partially_approved: { label: 'Partial', className: 'bg-amber-100 text-amber-700' },
  denied: { label: 'Denied', className: 'bg-red-100 text-red-700' },
  appealed: { label: 'Appealed', className: 'bg-purple-100 text-purple-700' },
  expired: { label: 'Expired', className: 'bg-gray-100 text-gray-500' },
  exhausted: { label: 'Exhausted', className: 'bg-orange-100 text-orange-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-400' },
};

export const ALL_AUTH_STATUSES = Object.keys(STATUS_BADGES);

export const ACTIVE_STATUSES = ['initiated', 'submitted', 'pending_review', 'approved', 'partially_approved'];

// Effective statuses that no longer draw visits (used for case grouping).
export const TERMINAL_STATUSES = ['denied', 'appealed', 'expired', 'exhausted', 'cancelled'];

// Server-derived lifecycle status (expired past expiration_date, exhausted at cap)
// with the stored workflow status as fallback for legacy responses.
export const getEffectiveStatus = (auth) => auth._effective_status ?? auth.status;

export const getUsagePercent = (auth) => {
  if (auth.auth_type === 'visit_based' && auth.visits_authorized) {
    const used = auth._visits_used_auto ?? 0;
    return Math.round((used / auth.visits_authorized) * 100);
  }
  if ((auth.auth_type === 'treatment_based' || auth.auth_type === 'referral_based') && auth.units_authorized) {
    const used = auth.units_used ?? 0;
    return Math.round((used / auth.units_authorized) * 100);
  }
  return null;
};

export const getDaysRemaining = (auth) => {
  if (!auth.expiration_date) return null;
  const diff = auth.expiration_date - Date.now();
  return Math.ceil(diff / 86400000);
};

const normalize = (val) => (val || '').toString().trim().toLowerCase();

// Match an auth's insurance snapshot (member_id, payer_name) to the patient's
// live insurance entry. member_id is the stable key; payer_name is the fallback.
export const matchAuthToInsurance = (auth, insuranceList) => {
  if (!Array.isArray(insuranceList) || insuranceList.length === 0) return null;

  const memberId = normalize(auth.member_id);
  if (memberId) {
    const index = insuranceList.findIndex(ins => normalize(ins.member_id) === memberId);
    if (index !== -1) return { entry: insuranceList[index], index };
  }

  const payerName = normalize(auth.payer_name);
  if (payerName) {
    const index = insuranceList.findIndex(ins => normalize(ins.payer_name) === payerName);
    if (index !== -1) return { entry: insuranceList[index], index };
  }

  return null;
};

// 'not_marked' = matching insurance exists but the auth requirement has never been
//   checked (neither required nor explicitly confirmed not-required),
// 'not_required' = someone explicitly checked and confirmed no auth is required
//   (requires_prior_auth false + auth_requirement_checked true),
// 'no_match' = no insurance entry matches the auth's snapshot, null = required (nothing to flag)
export const getInsuranceAuthFlag = (auth, patient) => {
  const match = matchAuthToInsurance(auth, patient?.insurance);
  if (!match) return 'no_match';
  if (match.entry.requires_prior_auth) return null;
  return match.entry.auth_requirement_checked ? 'not_required' : 'not_marked';
};

// The auth visits are actually drawn from, mirroring backend attribution:
// approved auths are consumed in GRANT ORDER (created_at ASC), skipping any
// that are exhausted or date-expired. By default falls back to the oldest
// approved auth so callers always get something to display when only spent
// auths remain; pass { allowSpent: false } to get null in that situation.
export const getDrawnFromAuth = (auths, { allowSpent = true } = {}) => {
  const approved = (auths || [])
    .filter(a => a.status === 'approved' || a.status === 'partially_approved')
    .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
  if (approved.length === 0) return null;

  const drawable = approved.find(a => {
    const usage = getUsagePercent(a);
    if (usage !== null && usage >= 100) return false;
    const daysLeft = getDaysRemaining(a);
    if (daysLeft !== null && daysLeft <= 0) return false;
    return true;
  });
  if (drawable) return drawable;
  return allowSpent ? approved[0] : null;
};

// The cases an auth is linked to: case_ids (several cases, case_id = its first entry,
// the primary case), else the single case_id on legacy rows, else none.
export const getAuthCaseIds = (auth) => {
  const ids = Array.isArray(auth?.case_ids) ? auth.case_ids.map(Number).filter(id => id > 0) : [];
  if (ids.length) return [...new Set(ids)];
  return auth?.case_id != null && Number(auth.case_id) > 0 ? [Number(auth.case_id)] : [];
};

// Primary auth of a set: newest non-terminal auth by effective status, else newest overall.
export function pickPrimaryAuth(members) {
  let primary = members[0];
  for (const a of members.slice(1)) {
    const aActive = !TERMINAL_STATUSES.includes(getEffectiveStatus(a));
    const pActive = !TERMINAL_STATUSES.includes(getEffectiveStatus(primary));
    if (aActive && !pActive) primary = a;
    else if (aActive === pActive && (a.created_at || 0) > (primary.created_at || 0)) primary = a;
  }
  return primary;
}

// Group auths by case: one group per linked case (an auth linked to several cases is a
// member of each of its case groups; auths without a case stand alone). `caseId` is
// null for case-less groups. Lifted from the admin Prior Authorizations page so the
// chart renders the same pick.
export function groupAuthsByCase(auths) {
  const groupsByKey = new Map();
  for (const auth of auths || []) {
    const caseIds = getAuthCaseIds(auth);
    const keys = caseIds.length ? caseIds.map(id => [`case-${id}`, id]) : [[`auth-${auth.id}`, null]];
    for (const [key, caseId] of keys) {
      if (!groupsByKey.has(key)) groupsByKey.set(key, { caseId, members: [] });
      groupsByKey.get(key).members.push(auth);
    }
  }
  return [...groupsByKey.entries()].map(([key, { caseId, members }]) => {
    const primary = pickPrimaryAuth(members);
    return { key, caseId, primary, siblings: members.filter(a => a !== primary) };
  });
}

export const getUsageLabel = (auth) => {
  if (auth.auth_type === 'visit_based' && auth.visits_authorized) {
    const used = auth._visits_used_auto ?? 0;
    return `${used}/${auth.visits_authorized} visits`;
  }
  if ((auth.auth_type === 'treatment_based' || auth.auth_type === 'referral_based') && auth.units_authorized) {
    const used = auth.units_used ?? 0;
    return `${used}/${auth.units_authorized} ${auth.unit_type || 'units'}`;
  }
  return null;
};
